import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import ModalProps from './Modal.types';
import styles from './Modal.module.scss';
import Button from '../../atoms/Button';
import Icon from '../../atoms/Icon';

const closeIcon = {
  width: '14',
  height: '14',
  icon: 'close-tooltip',
  viewBox: '0 0 14 14',
  title: 'Close',
};

/**
 * @TODO - Find a better way to document in stoprybook
 *    This is a temporary solution documentation solution
 * closedOnLoad - The modal will be close when the page loaded
 * trigger - A JSX element that opens the  modal via the onClick event
 * showCloseButton - Show or hide the X in the top right
 * pageContentSelector - A html selector string that is used to hide the
 *    background page from screen readers by adding a aria-hidden="true"
 *    attribute, typically this would be the app root, as the modal is
 *    created as a direct child of the body tag (via portals).
 * modalLabel - a title for the modal, this is optional and not displayed
 *    it is read by screen readers
 * clickOffToClose - click the background mask to close the modal
 *
 * Note: If a child element has the attibute data-modalclose it will have
 *    an onclick event added to it which will close the modal
 *
 * Theming setup: --white-color
 *
 */
const Modal = ({
  children,
  closedOnload,
  showCloseButton = true,
  pageContentSelector,
  trigger,
  clickOffToClose,
  escToClose = true,
  className,
  closeModalClickHandler,
  label = 'modal',
  dataCy = 'modal',
}: ModalProps) => {
  const [modalIsClosed, setModalIsClosed] = useState(closedOnload);
  const modalEl = useRef(null);

  const FOCUSABLE_ELEMENT_SELECTORS =
    'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, [tabindex="0"], [contenteditable]';
  const KEYCODE_TAB = 9;
  let focusableEls: any;
  let firstFocusableEl: any;
  let lastFocusableEl: any;

  // Runs on keypress - event removed on cleanup
  const keyPressAction = (e: any) => {
    const isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB;

    if (e.key === 'Escape' && escToClose) {
      setModalIsClosed(true);
      if (closeModalClickHandler) closeModalClickHandler();
    }

    if (!isTabPressed && focusableEls) {
      return;
    }

    if (e.shiftKey) {
      if (document.activeElement === firstFocusableEl) {
        /* shift + tab */
        lastFocusableEl.focus();
        e.preventDefault();
      }
    } else if (
      /* tab */
      document.activeElement === lastFocusableEl
    ) {
      firstFocusableEl.focus();
      e.preventDefault();
    }
  };

  // When the modal is open the background page content needs aria-hidden
  const setAriaValues = (selector: any) => {
    const el = document.querySelector(selector);
    if (!modalIsClosed && el) {
      el.setAttribute('aria-hidden', 'true');
    } else if (el) {
      el.setAttribute('aria-hidden', 'false');
    }
  };

  // look for close buttons in the content and attach an close function
  const attachCloseEvents = () => {
    const closeButtons = modalEl.current.querySelectorAll('[data-modalclose]');
    closeButtons.forEach((btns: any) => {
      btns.addEventListener('click', () => {
        // TODO remember include data-modalclose when implementing https://liberis.atlassian.net/browse/MX-567
        setModalIsClosed(true);
        if (closeModalClickHandler) closeModalClickHandler();
      });
    });
  };

  // Set up the  focus
  const setUpKeyPressEvents = () => {
    // implement a focus trap
    focusableEls = modalEl.current.querySelectorAll(FOCUSABLE_ELEMENT_SELECTORS);
    firstFocusableEl = focusableEls[0] || null;
    lastFocusableEl = focusableEls[focusableEls.length - 1] || null;
    firstFocusableEl.focus();
    modalEl.current.addEventListener('keydown', keyPressAction);
  };

  const setUp = () => {
    attachCloseEvents();
    setUpKeyPressEvents();
    setAriaValues(pageContentSelector);
  };

  const cleanUp = () => {
    if (modalEl.current) modalEl.current.removeEventListener('keydown', keyPressAction);
    setAriaValues(pageContentSelector);
  };

  useEffect(() => {
    if (modalEl.current && !modalIsClosed) {
      setUp();
    }

    return () => {
      cleanUp();
    };
  }, []);

  useEffect(() => {
    if (modalIsClosed) {
      cleanUp();
    } else {
      setUp();
    }
  }, [modalIsClosed]);

  const child = (
    <div
      role='dialog'
      aria-label={label}
      aria-modal='true'
      className={styles.modalContainer}
      ref={modalEl}
      data-cy={dataCy}>
      <div
        className={styles.modalMask}
        onClick={() => {
          if (clickOffToClose) setModalIsClosed(true);
        }}
        role='button'
        tabIndex={0}
        aria-label='Close modal window'
        onKeyPress={() => {
          if (clickOffToClose) setModalIsClosed(true);
        }}
      />
      <div className={cx(styles.modal, className)}>
        {showCloseButton ? (
          <Button
            variant='icon'
            aria-label='Close modal window'
            className={styles.closeButton}
            data-modalclose
            dataCy='closeButton'>
            <Icon {...closeIcon} />
          </Button>
        ) : null}
        {children}
      </div>
    </div>
  );

  const clonedTrigger = trigger
    ? React.cloneElement(trigger, {
        onClick: (e: any) => {
          e.preventDefault();
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();

          setModalIsClosed(false);
          if (closeModalClickHandler) closeModalClickHandler();
        },
      })
    : null;

  return !modalIsClosed ? (
    <>
      {clonedTrigger}
      {createPortal(child, document.body)}
    </>
  ) : (
    <>{clonedTrigger}</>
  );
};

export default Modal;
